import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, Tv, Zap, Shield, Clock, Star, BadgePercent } from "lucide-react";

const AbonnementIptv12Mois = () => {
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Abonnement IPTV 12 Mois - Roi du Streaming",
    "description": "Abonnement IPTV annuel avec les meilleures économies. Code Xtream livré automatiquement. +15000 chaînes HD/4K, VOD, sports. Tranquillité sur 1 an.",
    "image": ["https://roidustreaming.fr/favicon.png"],
    "brand": { "@type": "Brand", "name": "Roi du Streaming" },
    "offers": {
      "@type": "Offer",
      "name": "Abonnement IPTV 12 Mois",
      "price": "59.99",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": "https://roidustreaming.fr/abonnement-iptv-12-mois",
      "priceValidUntil": "2026-12-31",
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "FR",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" },
        "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "FR" },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "h" },
          "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "h" }
        }
      }
    },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "1523", "bestRating": "5", "worstRating": "1" }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://roidustreaming.fr" },
      { "@type": "ListItem", "position": 2, "name": "Nos Offres", "item": "https://roidustreaming.fr/nos-offres" },
      { "@type": "ListItem", "position": 3, "name": "Abonnement IPTV 12 Mois", "item": "https://roidustreaming.fr/abonnement-iptv-12-mois" }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Abonnement IPTV 12 Mois – Économies maximales | Roi du Streaming</title>
        <meta name="description" content="Abonnement IPTV annuel à prix réduit. Code Xtream livré instantanément. 1 an de tranquillité avec +15000 chaînes HD/4K, VOD et sports en direct." />
        <meta name="keywords" content="abonnement iptv 12 mois, iptv annuel, iptv 1 an pas cher, meilleur iptv annuel, code xtream 12 mois" />
        <link rel="canonical" href="https://roidustreaming.fr/abonnement-iptv-12-mois" />
        <meta property="og:title" content="Abonnement IPTV 12 Mois – Économies maximales" />
        <meta property="og:description" content="Abonnement IPTV annuel à prix réduit. 1 an de tranquillité avec +15000 chaînes HD/4K." />
        <meta property="og:url" content="https://roidustreaming.fr/abonnement-iptv-12-mois" />
        <meta property="og:type" content="product" />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header />

      <main className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="text-sm text-muted-foreground mb-8" aria-label="Fil d'Ariane">
            <ol className="flex flex-wrap gap-2">
              <li><Link to="/" className="hover:text-primary transition-colors">Accueil</Link></li>
              <li>/</li>
              <li><Link to="/nos-offres" className="hover:text-primary transition-colors">Nos Offres</Link></li>
              <li>/</li>
              <li className="text-foreground">Abonnement 12 Mois</li>
            </ol>
          </nav>

          {/* Hero Section */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-amber-500/10 text-amber-400 text-sm font-medium mb-4">
              <Star className="w-4 h-4 inline mr-1" />
              Économies maximales
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              Abonnement IPTV 12 Mois – <span className="gradient-text">Tranquillité totale</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              L'abonnement annuel pour ceux qui veulent profiter pleinement de leur IPTV sans se soucier du renouvellement. 
              Un an complet d'accès illimité avec les meilleures économies.
            </p>
          </div>

          {/* Price Card */}
          <div className="card-premium p-8 rounded-2xl mb-12 text-center border-2 border-amber-500/30 relative overflow-hidden">
            <div className="absolute top-4 right-4 px-3 py-1 bg-amber-500 text-white text-sm font-medium rounded-full flex items-center gap-1">
              <BadgePercent className="w-4 h-4" />
              -40%
            </div>
            <div className="text-5xl md:text-6xl font-bold text-primary mb-2">59,99€</div>
            <p className="text-muted-foreground mb-2">pour 12 mois d'accès complet</p>
            <p className="text-sm text-amber-400 mb-6">Soit seulement 5€/mois – le meilleur tarif</p>
            <Link to="/nos-offres">
              <Button className="btn-primary text-white font-semibold px-8 py-4 text-lg">
                Souscrire maintenant
              </Button>
            </Link>
          </div>

          {/* Content Section */}
          <div className="prose prose-lg prose-invert max-w-none mb-12">
            <p>
              L'abonnement 12 mois est fait pour ceux qui veulent profiter de leur IPTV en toute tranquillité 
              sur le long terme. Avec les économies les plus importantes de notre catalogue, vous bénéficiez 
              d'un accès annuel complet pour le prix de quelques mois.
            </p>
            <p>
              Comme pour toutes nos formules, votre code Xtream est généré et envoyé automatiquement après 
              validation de votre commande. Vous pouvez commencer à regarder immédiatement, sans aucune 
              manipulation complexe.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Tv className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">+15 000 chaînes</h3>
                <p className="text-sm text-muted-foreground">Accès illimité pendant 1 an</p>
              </div>
            </div>
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Qualité HD & 4K</h3>
                <p className="text-sm text-muted-foreground">La meilleure qualité d'image</p>
              </div>
            </div>
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">VOD illimitée</h3>
                <p className="text-sm text-muted-foreground">Films, séries, documentaires</p>
              </div>
            </div>
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Sports complets</h3>
                <p className="text-sm text-muted-foreground">Toute la saison sportive couverte</p>
              </div>
            </div>
          </div>

          {/* Benefits List */}
          <div className="card-premium p-8 rounded-2xl mb-12">
            <h2 className="text-2xl font-bold mb-6">Pourquoi choisir 12 mois ?</h2>
            <ul className="space-y-4">
              {[
                "Prix le plus avantageux : seulement 5€/mois",
                "Économisez 40% par rapport à l'offre 3 mois",
                "12 mois de tranquillité totale sans renouvellement",
                "Couvrez toute la saison sportive en une seule fois",
                "Accès complet à toutes les chaînes et à la VOD",
                "Livraison automatique immédiate par email",
                "Support client prioritaire inclus"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Comparison Note */}
          <div className="bg-muted/30 rounded-xl p-6 mb-12">
            <h3 className="font-semibold mb-3">Comparaison des économies</h3>
            <div className="grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <p className="text-muted-foreground">3 mois</p>
                <p className="font-medium">8,33€/mois</p>
              </div>
              <div>
                <p className="text-muted-foreground">6 mois</p>
                <p className="font-medium">5,83€/mois</p>
              </div>
              <div className="bg-amber-500/10 rounded-lg p-2">
                <p className="text-amber-400">12 mois</p>
                <p className="font-bold text-amber-400">5€/mois</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <p className="text-muted-foreground mb-6">
              Une question ? Notre support est disponible directement sur le site.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/nos-offres">
                <Button className="btn-primary text-white font-semibold px-8">
                  Voir toutes les offres
                </Button>
              </Link>
              <Link to="/faq">
                <Button variant="outline" className="px-8">
                  Questions fréquentes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AbonnementIptv12Mois;
